import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { SlotCalendar, fmtCz } from "@/components/SlotCalendar";
import { Loader2, CheckCircle2, ChevronLeft, MapPin, Phone, ShieldCheck, Wrench } from "lucide-react";

const STEPS = ["Vchod", "Byt", "Termín", "Kontakt", "Souhrn"];

export default function ResidentWizard() {
  const { token } = useParams();
  const [phase, setPhase] = useState("code"); // code | wizard | done | closed
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    document.title = meta?.svj_name ? `Servis oken – ${meta.svj_name} | OKENFIX` : "OKENFIX – Rezervace servisu oken pro SVJ";
    return () => { document.title = "OKENFIX – Rezervace servisu oken pro SVJ"; };
  }, [meta?.svj_name]);
  const [rtoken, setRtoken] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [entrances, setEntrances] = useState([]);
  const [entLoading, setEntLoading] = useState(true);
  const [entrance, setEntrance] = useState(null);
  const [apt, setApt] = useState(null);
  const [slotDays, setSlotDays] = useState([]);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ email: "", phone: "", name: "", note: "" });
  const [idem] = useState(() => `${token}-${Math.random().toString(36).slice(2)}-${Date.now()}`);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const goStep = (s) => { if (s < step) setStep(s); };

  useEffect(() => {
    if (phase !== "wizard") return;
    window.history.pushState({ step }, "");
    const onPop = () => { setStep((s) => Math.max(0, s - 1)); };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [phase, step]);

  const rapi = useMemo(() => axios.create({ baseURL: API, headers: { Authorization: `Bearer ${rtoken}` } }), [rtoken]);

  useEffect(() => {
    axios.get(`${API}/public/resolve/${token}`)
      .then((r) => { setMeta(r.data); if (r.data.action_status !== "zverejneno") setPhase("closed"); })
      .catch((e) => { setMeta({ error: apiErr(e) }); });
  }, [token]);

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/public/verify/${token}`, { code: code.trim() });
      setRtoken(data.token);
      setMeta((m) => ({ ...m, ...data }));
      setPhase("wizard");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (phase === "wizard" && rtoken) {
      rapi.get("/public/entrances").then((r) => setEntrances(r.data)).catch((e) => toast.error(apiErr(e))).finally(() => setEntLoading(false));
    }
  }, [phase, rtoken]);

  const chooseEntrance = (ent) => { setEntrance(ent); setApt(null); setSlot(null); setSlotDays([]); setStep(1); };
  const chooseApt = (a, ent = entrance) => {
    if (!a.available) { toast.info(`Pro tento byt již existuje rezervace – každý byt lze rezervovat pouze jednou. Pro změnu kontaktujte předsedu SVJ:\n${meta.contact || ""}`, { duration: 8000 }); return; }
    if (ent !== entrance) setEntrance(ent);
    setApt(a); setSlot(null); setStep(2);
    rapi.get(`/public/slots?entrance_id=${ent.id}`).then((r) => setSlotDays(r.data)).catch((e) => toast.error(apiErr(e)));
  };
  const [q, setQ] = useState("");
  const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const hits = useMemo(() => {
    const n = norm(q.trim());
    if (n.length < 2) return [];
    const src = step === 1 && entrance ? [entrance] : entrances;
    return src.flatMap((e) => e.floors.flatMap((f) => f.apartments.filter((a) => norm(a.owner_name).includes(n) || a.number === q.trim()).map((a) => ({ a, e, f })))).slice(0, 12);
  }, [q, entrances, entrance, step]);
  const hasOwners = entrances.some((e) => e.floors.some((f) => f.apartments.some((a) => a.owner_name)));
  const searchBox = hasOwners && (
    <div className="mb-4 space-y-2" data-testid="apt-search">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat byt podle příjmení nebo čísla…" className="h-11" data-testid="apt-search-input" />
      {hits.length > 0 && (
        <div className="border rounded-lg divide-y bg-white" data-testid="apt-search-results">
          {hits.map(({ a, e, f }) => (
            <button key={a.id} onClick={() => { setQ(""); chooseApt(a, e); }} disabled={!a.available} data-testid={`apt-hit-${e.label}-${a.number}`}
              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 ${a.available ? "hover:bg-slate-50 text-[#0A2540]" : "text-slate-400"}`}>
              <span><b>Byt {a.number}</b> · vchod {e.label} · patro {f.label}</span>
              <span className="truncate">{a.owner_name}{!a.available ? " (rezervováno)" : ""}</span>
            </button>
          ))}
        </div>
      )}
      {q.trim().length >= 2 && hits.length === 0 && <p className="text-xs text-slate-500" data-testid="apt-search-empty">Nic nenalezeno – zkuste jiné příjmení nebo vyberte byt ručně.</p>}
    </div>
  );
  const chooseSlot = (s, date) => { setSlot({ ...s, date }); setStep(3); };

  const submit = async () => {
    if (!/^\+?[0-9][0-9 ]{6,18}[0-9]$/.test(form.phone.trim())) return toast.error("Zadejte platné telefonní číslo");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) return toast.error("Zadejte platný e-mail");
    if (form.name.trim().length < 2) return toast.error("Vyplňte jméno na zvonku");
    setSubmitting(true);
    try {
      const { data } = await rapi.post("/public/reservations", {
        apartment_id: apt.id, slot_id: slot.id, email: form.email.trim(),
        phone: form.phone.trim(), name: form.name.trim(), note: form.note.trim(), idempotency_key: idem,
      });
      setResult(data); setPhase("done");
    } catch (err) {
      const msg = apiErr(err);
      toast.error(msg);
      if (err?.response?.status === 409) {
        // last spot taken concurrently: refresh slots, keep contacts
        rapi.get(`/public/slots?entrance_id=${entrance.id}`).then((r) => { setSlotDays(r.data); setSlot(null); setStep(2); });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!meta) return <div className="min-h-screen grid place-items-center text-slate-500">Načítání…</div>;
  if (meta.error) return <Shell><p className="text-red-600 text-center">{meta.error}</p></Shell>;

  if (phase === "closed")
    return <Shell><Header meta={meta} /><div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-amber-800 text-center whitespace-pre-line" data-testid="closed-notice">
      {meta.texts?.closed_text || "Registrace pro tuto akci nejsou aktuálně otevřené. Kontaktujte prosím předsedu SVJ."}
      {meta.contact && <span className="block mt-2 whitespace-pre-line">{meta.contact}</span>}
      {meta.service_contact && <span className="block mt-1 text-sm">Servisní tým: {meta.service_contact}</span>}
    </div></Shell>;

  if (phase === "code")
    return (
      <Shell>
        <Header meta={meta} />
        <form onSubmit={verify} className="space-y-4" data-testid="code-form">
          <Label className="text-base">Zadejte přístupový kód SVJ</Label>
          <Input value={code} onChange={(e) => setCode(e.target.value)} className="h-14 text-lg text-center tracking-widest"
            placeholder="KÓD" data-testid="code-input" />
          <Button type="submit" disabled={loading} className="w-full h-14 text-base" style={{ background: "#0A2540" }} data-testid="code-submit">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Pokračovat
          </Button>
        </form>
        <div className="mt-6"><Privacy meta={meta} /></div>
      </Shell>
    );

  if (phase === "done")
    return (
      <Shell>
        <div className="text-center space-y-4" data-testid="reservation-success">
          <CheckCircle2 className="mx-auto h-16 w-16 text-emerald-500" />
          <h2 className="text-2xl font-bold text-[#0A2540]">Rezervace je potvrzena</h2>
          <div className="bg-slate-50 border rounded-lg p-5 text-left space-y-1">
            <p className="text-lg"><b>Číslo rezervace:</b> <span className="okenfix-brand" style={{ color: "#C5A059" }}>{result.code}</span></p>
            <p><b>Termín:</b> {fmtCz(result.date)} {result.start}–{result.end}</p>
            <p><b>Byt:</b> {apt?.number}{entrance ? ` · vchod ${entrance.label}` : ""}</p>
          </div>
          <p className="text-slate-600 whitespace-pre-line text-left" data-testid="success-message">{result.message}</p>
          {(result.instructions || meta.instructions) && <div className="bg-slate-50 border rounded-lg p-4 text-left text-sm text-slate-700 whitespace-pre-line" data-testid="success-instructions"><b>Pokyny:</b>{"\n"}{result.instructions || meta.instructions}</div>}
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 text-left whitespace-pre-line" data-testid="success-spam-note">{result.spam_note || "Potvrzení jsme odeslali na Váš e-mail a SMS na uvedený telefon. Pokud e-mail neobdržíte, prosím zkontrolujte složku SPAM / hromadná pošta."}</p>
          {result.contact && <p className="text-slate-700 whitespace-pre-line text-left" data-testid="success-contact"><Phone className="h-4 w-4 inline mr-1" />Kontakt: {"\n"}{result.contact}</p>}
          {result.service_contact && <p className="text-slate-600 text-sm text-left" data-testid="success-service-contact"><Wrench className="h-4 w-4 inline mr-1" />Servisní tým: {result.service_contact}</p>}
        </div>
      </Shell>
    );

  return (
    <Shell wide>
      <Header meta={meta} />
      <Stepper step={step} onStep={goStep} />
      <div className="mt-6">
        {step > 0 && (
          <button onClick={() => setStep(step - 1)} className="mb-4 inline-flex items-center gap-1 h-10 px-4 rounded-full border-2 border-slate-200 text-[#0A2540] font-medium hover:border-[#0A2540] transition-colors" data-testid="wizard-back">
            <ChevronLeft className="h-4 w-4" /> Zpět na {STEPS[step - 1].toLowerCase()}
          </button>
        )}

        {step === 0 && searchBox}
        {step === 0 && (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="step-entrance">
            {entrances.map((e) => (
              <button key={e.id} onClick={() => chooseEntrance(e)} data-testid={`entrance-${e.label}`}
                className="p-5 rounded-xl border-2 text-left hover:border-[#0A2540] transition-colors bg-white">
                <div className="text-xl font-bold text-[#0A2540]">Vchod {e.label}</div>
                <div className="text-slate-500">{e.floors.length} pater</div>
              </button>
            ))}
            {entLoading && <p className="text-slate-500 flex items-center gap-2" data-testid="entrances-loading"><Loader2 className="h-4 w-4 animate-spin" /> Načítám vchody a byty…</p>}
            {!entLoading && entrances.length === 0 && <p className="text-slate-500">Žádné dostupné vchody.</p>}
          </div>
        )}

        {step === 1 && entrance && (
          <div className="space-y-5" data-testid="step-apartment">
            {searchBox}
            {entrance.floors.some((f) => f.apartments.some((a) => !a.available)) && (
              <p className="text-xs text-slate-500 bg-slate-50 border rounded p-3 whitespace-pre-line" data-testid="booked-info">
                {meta.texts?.booked_text || "Každý byt lze rezervovat pouze jednou. Šedě označené byty již rezervaci mají – pro změnu kontaktujte předsedu SVJ:"}{"\n"}{meta.contact}
              </p>
            )}
            {entrance.floors.map((f) => (
              <div key={f.label}>
                <div className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-2">Patro {f.label}</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {f.apartments.map((a) => (
                    <button key={a.id} onClick={() => chooseApt(a)} disabled={!a.available} data-testid={`apt-${a.number}`}
                      className={`min-h-14 px-2 py-2 rounded-lg border-2 text-left ${a.available ? "bg-white hover:border-[#0A2540] text-[#0A2540]" : "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed"}`}>
                      <div className="font-semibold">Byt {a.number}{!a.available && <span className="ml-1 text-[10px] uppercase tracking-wide" data-testid={`apt-booked-${a.number}`}>· rezervováno</span>}</div>
                      {a.owner_name && <div className="text-xs leading-tight truncate" data-testid={`apt-owner-${a.number}`}>{a.owner_name}</div>}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5" data-testid="step-slot">
            {slotDays.length === 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 text-amber-800 whitespace-pre-line">
                {meta.texts?.no_slots_text || "Momentálně nejsou volné termíny. Kontaktujte prosím předsedu SVJ:"} <span className="block">{meta.contact}</span>
                {meta.service_contact && <span className="block mt-1">Servisní tým: {meta.service_contact}</span>}
              </div>
            )}
            {slotDays.length > 0 && <SlotCalendar days={slotDays} onPick={chooseSlot} />}
            {slotDays.length > 0 && (meta.instructions || meta.contact) && (
              <p className="text-xs text-slate-500 whitespace-pre-line border-t pt-3" data-testid="slot-help">
                <b>Nehodí se Vám žádný z nabízených termínů?</b>{"\n"}{meta.instructions ? `${meta.instructions}\n` : ""}{meta.contact && !(meta.instructions || "").includes((meta.contact || "").split("\n")[0].trim()) ? `Kontaktujte prosím předsedu SVJ:\n${meta.contact}\n` : ""}{meta.service_contact && !(meta.instructions || "").includes(meta.service_contact) ? `Servisní tým: ${meta.service_contact}` : ""}
              </p>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4 max-w-md" data-testid="step-contact">
            <div className="space-y-2">
              <Label>E-mail *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12" data-testid="contact-email" />
            </div>
            <div className="space-y-2">
              <Label>Telefon *</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-12" placeholder="+420 601 123 456" data-testid="contact-phone" />
            </div>
            <div className="space-y-2">
              <Label>Jméno na zvonku *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12" placeholder="např. Novákovi" data-testid="contact-name" />
            </div>
            <div className="space-y-2">
              <Label>Poznámka (nepovinné, např. počet oken)</Label>
              <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="contact-note" />
            </div>
            <Button className="w-full h-12" style={{ background: "#0A2540" }} disabled={!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim()) || !form.phone.trim() || form.name.trim().length < 2} onClick={() => setStep(4)} data-testid="to-summary">Pokračovat na souhrn</Button>
            <p className="text-xs text-slate-500">E-mail, telefon i jméno na zvonku jsou povinné – bez nich nelze rezervaci dokončit (potvrzení a připomínka přijdou e-mailem i SMS).</p>
            <Privacy meta={meta} />
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4 max-w-md" data-testid="step-summary">
            <div className="bg-slate-50 border rounded-lg p-5 space-y-2">
              <Row k="SVJ" v={meta.svj_name} />
              <Row k="Vchod" v={entrance.label} onEdit={() => setStep(1)} testId="edit-entrance" />
              <Row k="Byt" v={apt.number} onEdit={() => setStep(1)} testId="edit-apt" />
              <Row k="Termín" v={`${fmtCz(slot.date)} ${slot.start}–${slot.end}`} onEdit={() => setStep(2)} testId="edit-slot" />
              <Row k="E-mail" v={form.email} onEdit={() => setStep(3)} testId="edit-contact" />
              <Row k="Telefon" v={form.phone} onEdit={() => setStep(3)} />
              {form.name && <Row k="Jméno na zvonku" v={form.name} />}
              {form.note && <Row k="Poznámka" v={form.note} />}
            </div>
            <Button className="w-full h-14 text-base" style={{ background: "#C5A059" }} onClick={submit} disabled={submitting} data-testid="confirm-reservation">
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Potvrdit rezervaci
            </Button>
            <p className="text-xs text-slate-500 text-center" data-testid="consent-note">Odesláním potvrzujete, že jste se seznámili s informacemi o zpracování údajů uvedenými níže.</p>
            <Privacy meta={meta} />
          </div>
        )}
      </div>
    </Shell>
  );
}

function Privacy({ meta }) {
  const days = meta.retention_days || 365;
  return (
    <details className="text-xs text-slate-500 border-t pt-3" data-testid="privacy-block">
      <summary className="cursor-pointer inline-flex items-center gap-1 font-medium text-slate-600"><ShieldCheck className="h-3.5 w-3.5" /> Informace o zpracování osobních údajů a kontakt provozovatele</summary>
      <div className="mt-2 space-y-2">
        <p>Vaše kontaktní údaje (e-mail, telefon, jméno, poznámka) zpracováváme výhradně za účelem sjednání a připomenutí termínu servisu oken pro SVJ {meta.svj_name}. Údaje jsou předány servisnímu týmu a předsedovi SVJ. Po uplynutí <b>{days} dnů</b> od termínu servisu jsou kontaktní údaje automaticky anonymizovány.</p>
        {meta.operator_info
          ? <p className="whitespace-pre-line text-slate-700" data-testid="operator-info-text">{meta.operator_info}</p>
          : <p className="italic">Kontaktní údaje provozovatele doplní správce systému.</p>}
        {meta.contact && <p className="whitespace-pre-line">Kontakt: {meta.contact}</p>}
        {meta.service_contact && <p data-testid="meta-service-contact">Servisní tým: {meta.service_contact}</p>}
      </div>
    </details>
  );
}

function Shell({ children, wide }) {
  return (
    <div className="min-h-screen py-8 px-4 bg-[#F8FAFC]">
      <div className={`mx-auto ${wide ? "max-w-2xl" : "max-w-md"}`}>
        <div className="okenfix-brand text-2xl font-extrabold mb-6 text-center" style={{ color: "#0A2540" }}>OKENFIX</div>
        <div className="bg-white rounded-2xl border shadow-sm p-6 sm:p-8">{children}</div>
      </div>
    </div>
  );
}

function Header({ meta }) {
  const firstContact = (meta.contact || "").split("\n")[0].trim();
  const hasContactInside = firstContact && (meta.instructions || "").includes(firstContact);
  return (
    <div className="mb-6">
      <h1 className="text-2xl font-bold text-[#0A2540]">{meta.svj_name}</h1>
      {meta.address && <p className="text-slate-500 flex items-center gap-1"><MapPin className="h-4 w-4" /> {meta.address}</p>}
      {meta.action_name && <p className="mt-1 font-medium text-[#C5A059]">{meta.action_name}</p>}
      {(meta.instructions || meta.contact) && (
        <div className="mt-2 text-slate-600 text-sm bg-slate-50 rounded p-3 whitespace-pre-line" data-testid="header-instructions">
          {meta.instructions && <p className={hasContactInside ? "" : "mb-2"}>{meta.instructions}</p>}
          {meta.contact && !hasContactInside && <p><b>Kontakt na předsedu / výbor SVJ:</b>{"\n"}{meta.contact}</p>}
          {meta.service_contact && !(meta.instructions || "").includes(meta.service_contact) && <p className="mt-1"><b>Servisní tým:</b> {meta.service_contact}</p>}
        </div>
      )}
    </div>
  );
}

function Stepper({ step, onStep }) {
  return (
    <div className="flex items-center gap-1" data-testid="stepper">
      {STEPS.map((s, i) => (
        <button key={s} onClick={() => onStep(i)} disabled={i >= step} data-testid={`stepper-${i}`}
          className={`flex-1 text-center ${i < step ? "cursor-pointer hover:opacity-70" : "cursor-default"}`} aria-label={i < step ? `Zpět na krok ${s}` : s}>
          <div className={`h-1.5 rounded-full ${i <= step ? "bg-[#0A2540]" : "bg-slate-200"}`} />
          <div className={`text-[11px] mt-1 ${i === step ? "text-[#0A2540] font-semibold" : i < step ? "text-[#0A2540] underline decoration-dotted" : "text-slate-400"}`}>{s}</div>
        </button>
      ))}
    </div>
  );
}

function Row({ k, v, onEdit, testId }) {
  return (
    <div className="flex justify-between gap-4 items-center">
      <span className="text-slate-500">{k}</span>
      <span className="font-medium text-right flex items-center gap-2">{v}
        {onEdit && <button onClick={onEdit} className="text-xs text-[#0A2540] underline decoration-dotted hover:opacity-70" data-testid={testId}>změnit</button>}
      </span>
    </div>
  );
}
